import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { publicContentAPI } from '../lib/api';
import { 
  GraduationCap, 
  ArrowLeft,
  HelpCircle,
  ChevronDown,
  Loader2
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const res = await publicContentAPI.getFaqs();
        setFaqs(res.data.faqs || []);
      } catch (err) {
        console.error('Failed to load FAQs:', err);
        setError('Failed to load FAQs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    loadFaqs();
  }, []);

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    const category = faq.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(faq);
    return acc;
  }, {});

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="faq-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                  Gifted Academy Tutors
                </span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 md:py-16 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center">
              <HelpCircle className="h-8 w-8 text-indigo-700" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-medium text-slate-900">
                Frequently Asked Questions
              </h1>
              <p className="text-slate-500">Find answers to common questions about our tutoring services</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
            </div>
          ) : faqs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
              <HelpCircle className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No FAQs available at the moment.</p>
              <p className="text-sm text-slate-400 mt-2">Please check back later or contact us directly.</p>
              <Link to="/contact">
                <Button className="mt-6 bg-indigo-600 hover:bg-indigo-700">Contact Us</Button>
              </Link>
            </div>
          ) : (
            <motion.div {...fadeUp} className="space-y-8">
              {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
                <div key={category} className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                  <div className="bg-slate-50 px-6 py-4 border-b border-stone-200">
                    <h2 className="font-heading text-lg font-semibold text-slate-900">{category}</h2>
                  </div>
                  <div className="divide-y divide-stone-100">
                    {categoryFaqs.map((faq) => (
                      <div key={faq.faq_id} className="px-6">
                        <button
                          onClick={() => toggleExpand(faq.faq_id)}
                          className="w-full py-5 flex items-start justify-between gap-4 text-left"
                          data-testid={`faq-item-${faq.faq_id}`}
                        >
                          <span className="font-medium text-slate-900">{faq.question}</span>
                          <ChevronDown 
                            className={`h-5 w-5 text-slate-400 shrink-0 transition-transform duration-200 ${
                              expandedId === faq.faq_id ? 'rotate-180' : ''
                            }`} 
                          />
                        </button>
                        {expandedId === faq.faq_id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pb-5"
                          >
                            <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{faq.answer}</p>
                          </motion.div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* Contact CTA */}
          <motion.div {...fadeUp} className="mt-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-center text-white">
            <h3 className="font-heading text-xl font-semibold mb-2">Still have questions?</h3>
            <p className="text-indigo-100 mb-6">Our team is here to help. Reach out anytime!</p>
            <Link to="/contact">
              <Button variant="secondary" className="bg-white text-indigo-700 hover:bg-indigo-50">
                Contact Us
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
